import { Alert, Box, Button, Chip, Grid, Paper, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { ToastService } from "src/Shared/Services";
import ActionModal from "src/Shared/Components/ActionModal/ActionModal";
import CompanyService from "../../Services/Company.service";
import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import { formatLocalDeviceDateTime } from "../../Tools/OnboardingOutputs";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  onChanged: () => void;
  frozen: boolean;
};

/**
 * The specialist review (story 5591): a reviewer edits this section, adds their own notes and marks
 * the review complete — nothing more. Completing hands the onboarding back to the relationship
 * manager without submitting it. While the review is pending, step 2 reads incomplete through the
 * shared completion rule, which is what blocks submission (story 5594).
 *
 * Rendered by Step2Services itself, against that component's own live `services.serviceLine`, rather
 * than by the wizard shell against its `state` snapshot — the shell's copy only refreshes on step
 * entry, so ticking Both and continuing to work never mounted this card in the same render; only
 * leaving the step and coming back did (bug 5833).
 */
const HealthTickReviewCard: React.FC<Props> = ({ state, companyRowID, onChanged, frozen }) => {
  const review = state.healthTickReview;
  const [spokeTo, setSpokeTo] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  useEffect(() => {
    setSpokeTo(review?.spokeTo || "");
    setPhoneNumber(review?.phoneNumber || "");
    setEmailAddress(review?.emailAddress || "");
    setNotes(review?.notes || "");
  }, [review]);

  const save = (complete: boolean) => {
    setSaving(true);
    CompanyService.saveOnboardingReview(companyRowID, complete, {
      spokeTo,
      phoneNumber,
      emailAddress,
      notes,
    }).subscribe({
      next: (response) => {
        setSaving(false);
        ToastService.success(response.message);
        onChanged();
      },
      error: (error: AxiosError) => {
        setSaving(false);
        ToastService.axiosError(error);
      },
    });
  };

  const pending = review?.status === 1;

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 2, border: "1px solid #e0e0e0" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          HealthTick review
        </Typography>
        {review?.status && (
          <Chip
            size="small"
            color={pending ? "warning" : "success"}
            label={pending ? "Pending" : "Complete"}
          />
        )}
      </Box>

      {review?.requestedOn && (
        <Typography variant="caption" color="text.secondary" display="block">
          Requested {formatLocalDeviceDateTime(review.requestedOn)}
          {review.requestedBy ? ` by ${review.requestedBy}` : ""}
        </Typography>
      )}
      {review?.completedOn && (
        <Typography variant="caption" color="text.secondary" display="block">
          Completed {formatLocalDeviceDateTime(review.completedOn)}
          {review.completedBy ? ` by ${review.completedBy}` : ""}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Spoke to"
            value={spokeTo}
            disabled={frozen}
            onChange={(e) => setSpokeTo(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Phone"
            value={phoneNumber}
            disabled={frozen}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            value={emailAddress}
            disabled={frozen}
            onChange={(e) => setEmailAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Reviewer notes"
            value={notes}
            disabled={frozen}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Stories 5627/5633: the one shared cross-system modal describes what the request does,
          and only the explicit confirm sets the review pending. It lists only what the save does —
          it used to promise a CRM action, an email and a reminder that nothing creates (TC-6358-04). */}
      <ActionModal
        open={confirmOpen}
        title="Send for HealthTick review?"
        items={[
          "Set the HealthTick review to Pending, stamped with your name and the time.",
          "Save who you spoke to, their contact details and the reviewer notes with the onboarding.",
        ]}
        note={
          "Nobody is notified automatically — let the HealthTick reviewer know it is waiting. While "
          + "the review is pending, Services & Testing reads incomplete and the onboarding cannot be "
          + "submitted. Marking the review complete releases that."
        }
        confirmLabel="Send for review"
        busy={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          save(false);
        }}
      />

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={frozen || saving || pending}
          onClick={() => setConfirmOpen(true)}
        >
          Send for HealthTick review
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={frozen || saving || !pending}
          onClick={() => save(true)}
        >
          Mark review complete
        </Button>
      </Box>

      {pending && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          While this review is pending, Services &amp; Testing reads incomplete and
          the onboarding cannot be submitted.
        </Alert>
      )}
    </Paper>
  );
};

export default HealthTickReviewCard;
